import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import { getBeneficiaryRepository } from "@/infrastructure/di";
import type { BeneficiaryFilterState } from "@/domain/types";
import type {
  BeneficiaryAggregateKey,
  BeneficiaryListFilters,
  BeneficiarySort,
} from "@/domain/beneficiary";
import { queryKeys } from "../queryKeys";
import { distinctBeneficiaryPages } from "./cache";

export const useBeneficiaries = (
  orgId: string,
  filters: BeneficiaryListFilters,
  sort: BeneficiarySort[],
  pageSize: number,
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.beneficiaries.list(orgId, { filters, sort, pageSize }),
    queryFn: ({ pageParam, signal }) =>
      getBeneficiaryRepository().getBeneficiaries(
        { ...filters, sort, limit: pageSize, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.beneficiaries.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    select: distinctBeneficiaryPages,
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
};

export const useBeneficiaryAggregates = (
  orgId: string,
  filters: BeneficiaryListFilters,
  aggregates: readonly BeneficiaryAggregateKey[],
) => {
  return useQuery({
    queryKey: queryKeys.beneficiaries.aggregates(orgId, { ...filters }, aggregates),
    queryFn: ({ signal }) =>
      getBeneficiaryRepository().getBeneficiaryAggregates(
        { ...filters, aggregates: [...aggregates] },
        signal,
      ),
    enabled: !!orgId && aggregates.length > 0,
    placeholderData: keepPreviousData,
  });
};

export const useBeneficiaryDetail = (orgId: string, id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.beneficiaries.detail(orgId, id!),
    queryFn: ({ signal }) => getBeneficiaryRepository().getBeneficiary(id!, signal),
    enabled: !!id && !!orgId,
  });
};

export const useBeneficiaryMetrics = (orgId: string, filters: BeneficiaryFilterState) => {
  const apiParams = {
    search: filters.search || undefined,
    status: filters.status || undefined,
    sector: filters.sector || undefined,
  };

  return useQuery({
    queryKey: queryKeys.beneficiaries.metrics(orgId, apiParams),
    queryFn: ({ signal }) => getBeneficiaryRepository().getBeneficiaryMetrics(apiParams, signal),
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
};
